import { useState, useEffect, useRef } from 'react';
import { useListMarkets, type Market } from '@workspace/api-client-react';
import { normalizeSearchTerms } from '@workspace/search-utils';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { PlatformBadge } from '@/components/platform-badge';
import { PriceBar } from '@/components/ui/price-bar';
import { Link } from 'wouter';
import { AlertTriangle, Search, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function MarketsPage() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [platform, setPlatform] = useState<'all' | 'polymarket' | 'kalshi'>('all');
  const [status, setStatus] = useState<'all' | 'active' | 'closed'>('active');
  /** When true, sends minScore=1 to broaden results for the current query. */
  const [broadSearch, setBroadSearch] = useState(false);
  const [cappedDismissed, setCappedDismissed] = useState(false);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset cap dismiss whenever the platform filter changes so the banner
  // can reappear if the new query/platform combo also hits the cap.
  useEffect(() => {
    setCappedDismissed(false);
  }, [platform]);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      // Reset broad search whenever the query changes so the threshold starts tight
      setBroadSearch(false);
      // Reset cap dismiss so a new query can show the banner if it hits the cap
      setCappedDismissed(false);
    }, 400);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [search]);

  const { data: marketsData, isLoading, isFetching, refetch } = useListMarkets({
    platform: platform === 'all' ? undefined : platform,
    status: status === 'all' ? undefined : status,
    search: debouncedSearch || undefined,
    limit: 50,
    ...(broadSearch ? { minScore: 1 } : {}),
  }, {
    // Poll every 25 s so Kalshi price updates (server cache TTL: 20 s) are
    // visible to the user within the 30-second freshness window.
    query: { refetchInterval: 25_000 },
  });
  const markets = marketsData?.markets;
  // Only show partial warning when:
  //  • Kalshi is included in the query (platform !== 'polymarket')
  //  • The last completed fetch reported partial results
  //  • A retry is not currently in-flight (isFetching) — the banner clears
  //    immediately when the user clicks Retry, and stays gone if the
  //    subsequent fetch returns kalshiPartial: false.
  const kalshiPartial =
    (marketsData?.kalshiPartial ?? false) && platform !== 'polymarket' && !isFetching;
  const kalshiProvisional =
    (marketsData?.kalshiProvisional ?? false) && platform !== 'polymarket' && !isFetching;
  // Show cap banner when Kalshi scan stopped at KALSHI_MAX_MARKETS and more pages exist.
  // Also hidden when Polymarket-only filter is active (Kalshi wasn't queried).
  const kalshiCapped =
    (marketsData?.kalshiCapped ?? false) && platform !== 'polymarket' && !isFetching;

  function formatAge(iso?: string | null): string | null {
    if (!iso) return null;
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 pb-24 lg:pb-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Prediction Markets</h1>
        <p className="text-muted-foreground mt-1">
          Discover and analyze markets across platforms
        </p>
      </div>

      {/* Search & Filters */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search markets..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="flex flex-wrap gap-4">
          {/* Platform Filter */}
          <div className="flex gap-2">
            <Button
              variant={platform === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPlatform('all')}
            >
              All Platforms
            </Button>
            <Button
              variant={platform === 'polymarket' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPlatform('polymarket')}
            >
              Polymarket
            </Button>
            <Button
              variant={platform === 'kalshi' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPlatform('kalshi')}
            >
              Kalshi
            </Button>
          </div>

          {/* Status Filter */}
          <div className="flex gap-2">
            <Button
              variant={status === 'active' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatus('active')}
            >
              Active
            </Button>
            <Button
              variant={status === 'closed' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatus('closed')}
            >
              Closed
            </Button>
          </div>
        </div>
      </div>

      {/* Partial-results notice — shown when Kalshi catalog scan was cut short */}
      {kalshiPartial && !isLoading && (
        <div className="flex items-start gap-3 rounded-lg border border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950/30 px-4 py-3 text-sm text-yellow-800 dark:text-yellow-300">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-yellow-500" />
          <span className="flex-1">
            {kalshiProvisional
              ? 'Preliminary results — a full scan hasn\'t completed yet. Some markets might not appear.'
              : 'Kalshi results may be incomplete — the catalog scan was cut short due to a network issue. Some markets might not appear.'}
          </span>
          <button
            onClick={() => refetch()}
            className="shrink-0 font-semibold underline underline-offset-2 hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Cap banner — shown when the Kalshi catalog scan hit KALSHI_MAX_MARKETS */}
      {kalshiCapped && !isLoading && !cappedDismissed && (
        <div className="flex items-start gap-3 rounded-lg border border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950/30 px-4 py-3 text-sm text-yellow-800 dark:text-yellow-300">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-yellow-500" />
          <span className="flex-1">
            Only the first {marketsData?.kalshiCappedAt ?? 'batch of'} Kalshi markets were searched. Try a more specific query for better results.
          </span>
          <button
            onClick={() => setCappedDismissed(true)}
            className="shrink-0 font-semibold underline underline-offset-2 hover:no-underline"
            aria-label="Dismiss"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Markets Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6 space-y-4">
                <div className="h-6 bg-muted rounded w-20" />
                <div className="h-12 bg-muted rounded" />
                <div className="h-10 bg-muted rounded" />
                <div className="flex justify-between">
                  <div className="h-4 bg-muted rounded w-24" />
                  <div className="h-4 bg-muted rounded w-16" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : markets && markets.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {markets.map((market: Market) => (
            <Card key={market.marketId} className="card-elevated border-0 group">
              <CardContent className="p-6 space-y-4">
                <PlatformBadge platform={market.platform} />
                
                <h3 className="font-semibold line-clamp-2 min-h-[3rem] group-hover:text-primary transition-colors">
                  {market.question}
                </h3>

                <PriceBar yesPrice={market.yesPrice} />

                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>Vol: ${market.volume >= 1_000_000 ? `${(market.volume/1_000_000).toFixed(1)}M` : market.volume >= 1000 ? `${(market.volume/1000).toFixed(0)}K` : market.volume.toFixed(0)}</span>
                  {market.closeDate && (
                    <span>{new Date(market.closeDate).toLocaleDateString()}</span>
                  )}
                </div>

                {market.lastUpdated && (
                  <p className="text-xs text-muted-foreground/70">
                    Prices {formatAge(market.lastUpdated)}
                  </p>
                )}

                <Link href={`/markets/${encodeURIComponent(market.marketId)}?platform=${market.platform}`}>
                  <Button className="w-full" style={{background:'hsl(239 84% 67%)', color:'white', border:'none'}}>
                    Analyze with Claude
                    <TrendingUp className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="h-12 w-12 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
              <Search className="h-6 w-6 text-muted-foreground" />
            </div>
            {debouncedSearch && normalizeSearchTerms(debouncedSearch).length === 0 ? (
              <>
                <h3 className="font-semibold mb-2">Search terms ignored</h3>
                <p className="text-sm text-muted-foreground">
                  Your search only contains common words that are ignored — try a more descriptive term or{' '}
                  <button
                    onClick={() => setSearch('')}
                    className="underline underline-offset-2 hover:no-underline"
                  >
                    clear the search
                  </button>
                  .
                </p>
              </>
            ) : debouncedSearch ? (
              <>
                <h3 className="font-semibold mb-2">No results for &ldquo;{debouncedSearch}&rdquo;</h3>
                <p className="text-sm text-muted-foreground">
                  Your search didn&rsquo;t match any markets. Try different keywords or{' '}
                  <button
                    onClick={() => setSearch('')}
                    className="underline underline-offset-2 hover:no-underline"
                  >
                    clear the search
                  </button>
                  .
                </p>
                {!broadSearch && (
                  <div className="mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setBroadSearch(true)}
                    >
                      Show broader results
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">
                      Includes loosely related markets that contain at least one search term
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                <h3 className="font-semibold mb-2">No markets found</h3>
                <p className="text-sm text-muted-foreground">
                  Try adjusting your filters or search query
                </p>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
